import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useStageStore, type Stage } from '../../../../stores/stageStore';

interface Props {
  stage: Stage;
  onClose: () => void;
  onDeleted: (stage: Stage) => void;
}

/*
  Ne s'ouvre que pour un stage sans aucune session (`peutEtreSupprime`) :
  l'ecran propose « Desactiver » pour les autres. La base garde le dernier
  mot, et son refus s'affiche ici.
*/
const StageDeleteModal = ({ stage, onClose, onDeleted }: Props) => {
  const { deleteStage, enregistrement } = useStageStore();
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => e.key === 'Escape' && !enregistrement && onClose();
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [enregistrement, onClose]);

  const supprimer = async () => {
    setErreur(null);
    try {
      await deleteStage(stage.id);
      onDeleted(stage);
    } catch (error) {
      console.error('Error deleting stage:', error);
      const message = (error as { message?: unknown } | null)?.message;
      setErreur(`Le stage n’a pas pu être supprimé${typeof message === 'string' ? ` (${message})` : ''}.`);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onMouseDown={(e) => e.target === e.currentTarget && !enregistrement && onClose()}
    >
      <div role="alertdialog" aria-modal="true" className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center mb-4">
          <div className="bg-red-100 rounded-full p-2 mr-3">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Supprimer « {stage.title} » ?</h3>
        </div>

        <p className="text-gray-600 mb-6">
          Ce stage n’a jamais eu de session. Sa fiche et sa photo seront définitivement supprimées.
        </p>

        {erreur && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-start text-sm" role="alert">
            <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
            <p>{erreur}</p>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          {/* Le focus va sur le choix sans consequence. */}
          <button onClick={onClose} className="btn-outline" disabled={enregistrement} autoFocus>
            Annuler
          </button>
          <button
            onClick={supprimer}
            className="btn-primary bg-red-600 hover:bg-red-700"
            disabled={enregistrement}
          >
            {enregistrement ? (
              <span className="flex items-center">
                <Loader2 className="animate-spin mr-2 h-4 w-4" />
                Suppression…
              </span>
            ) : (
              'Supprimer'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StageDeleteModal;
